import React from 'react';
import { Breadcrumbs, Link, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../utils/colors';

/**
 * PageBreadcrumbs Component
 * A reusable breadcrumbs component with consistent styling and responsive design
 *
 * @param {Array} items - Array of breadcrumb items
 * Each item should have: { label: string, path: string (optional) }
 * The last item is automatically rendered as the current page (non-clickable)
 */
const PageBreadcrumbs = ({ items = [] }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  if (!items || items.length === 0) {
    return null;
  }

  const handleClick = (event, path) => {
    event.preventDefault();
    if (path) {
      navigate(path);
    }
  };

  return (
    <Box
      sx={{
        mb: { xs: 2, sm: 3, md: 4 },
        mt: { xs: 0, sm: 0 },
        py: { xs: 1, sm: 1.5 },
        position: 'sticky',
        top: 0,
        bgcolor: 'background.default',
        zIndex: 10
      }}
    >
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ color: colors.text.secondary }} />}
        aria-label="breadcrumb"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: { xs: 0.5, sm: 1 }
          }
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          if (isLast) {
            return (
              <Typography
                key={index}
                sx={{
                  color: colors.text.primary,
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.125rem' },
                  lineHeight: 1.5
                }}
              >
                {item.label}
              </Typography>
            );
          }

          return (
            <Link
              key={index}
              underline="hover"
              color="inherit"
              href={item.path || '#'}
              onClick={(e) => handleClick(e, item.path)}
              sx={{
                color: colors.text.secondary,
                fontWeight: 500,
                fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
                cursor: item.path ? 'pointer' : 'default',
                transition: 'color 0.2s ease',
                '&:hover': {
                  color: item.path ? colors.primary.main : colors.text.secondary
                },
                textDecoration: 'none',
                lineHeight: 1.5
              }}
            >
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default PageBreadcrumbs;
